import axios from 'axios';
import { getPdfViewUrl } from './documentKnowledgeAPI';
import { v4 as uuidv4 } from 'uuid';

/**
 * Interface representing the JSON response format from the API
 */
interface ChatAPIResponseItem {
  response: string;
  finalOutput?: {
    page_number: number;
    mongodb_id: string;
    fileName: string;
    fileType: string;
  }[];
}

/**
 * Interface representing chat session data from the API
 */
interface SessionData {
  _id: string;
  sessionId: string;
  messages: {
    type: 'human' | 'ai';
    data: {
      content: string;
      additional_kwargs: any;
      response_metadata: any;
      tool_calls?: any[];
      invalid_tool_calls?: any[];
    };
  }[];
}

/**
 * Sends a chat message to the Agent GG API and retrieves the response
 * @param message The user's input message
 * @param useKnowledgeBase Whether to use the knowledge base for the response
 * @param sessionId The unique ID for the current chat session
 * @param files Optional array of files to upload with the message
 * @returns A Promise that resolves to the agent's response with sources
 */
export const sendChatMessage = async (
  message: string,
  useKnowledgeBase: boolean,
  sessionId: string,
  files?: File[]
): Promise<{ responseText: string; sources: any[] }> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No JWT token found in localStorage');
      throw new Error('Authentication error. Please log in again.');
    }

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const serverType = process.env.REACT_APP_SERVER_TYPE;
    const webhookPath = serverType === 'test' ? 'webhook-test' : 'webhook';

    // Construct the chat API endpoint
    const chatEndpoint = `${baseUrl}/${webhookPath}/agent_gg_aukik/chat`;

    // If there are files to upload, use FormData
    if (files && files.length > 0) {
      const formData = new FormData();

      // Add text fields
      formData.append('chatInput', message);
      formData.append('useKnowledgeBase', useKnowledgeBase.toString());
      formData.append('sessionId', sessionId);
      formData.append('fileSent', 'true'); // Indicate files are being sent

      // Add files
      files.forEach((file, index) => {
        formData.append(`file${index}`, file);
      });

      // Make the POST request with FormData
      const response = await axios.post(chatEndpoint, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type here, browser will set it with correct boundary for FormData
        }
      });

      // Process the response
      if (response.data) {
        // Check if the response is an array
        const responseData = Array.isArray(response.data) ? response.data[0] : response.data;

        if (!responseData) {
          throw new Error('Invalid response format from the server');
        }

        // Extract the response text and sources
        const responseText = responseData.response || '';
        const sourcesFromAPI = responseData.finalOutput || [];

        console.log('Response text:', responseText);
        console.log('Sources:', sourcesFromAPI);

        // Return both the response text and sources
        return {
          responseText,
          sources: sourcesFromAPI
        };
      } else {
        throw new Error('Invalid response format from the server');
      }
    } else {
      // No files - use regular JSON payload
      const payload = {
        chatInput: message,
        useKnowledgeBase: useKnowledgeBase,
        sessionId: sessionId,
        fileSent: "false" // Use string for consistency with FormData
      };

      // Make the POST request
      const response = await axios.post(chatEndpoint, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Process the response based on the new format
      if (response.data) {
        // Check if the response is an array
        const responseData = Array.isArray(response.data) ? response.data[0] : response.data;

        if (!responseData) {
          throw new Error('Invalid response format from the server');
        }

        // Extract the response text and sources
        const responseText = responseData.response || '';
        const sourcesFromAPI = responseData.finalOutput || [];

        console.log('Response text:', responseText);
        console.log('Sources:', sourcesFromAPI);

        // Return both the response text and sources
        return {
          responseText,
          sources: sourcesFromAPI
        };
      } else {
        throw new Error('Invalid response format from the server');
      }
    }
  } catch (error) {
    console.error('Error sending chat message:', error);
    throw error;
  }
};

/**
 * Fetches a PDF view URL for a document
 * @param id Document ID to fetch the URL for
 * @returns Promise that resolves to the PDF URL or null
 */
export const getPdfUrl = async (id: string): Promise<string | null> => {
  try {
    return await getPdfViewUrl(id);
  } catch (error) {
    console.error('Error fetching PDF URL:', error);
    return null;
  }
};

/**
 * Get direct access URL for PDF binary data
 * @param id MongoDB document ID
 * @returns URL that directly serves the PDF binary data
 */
export const getPdfDirectUrl = (id: string): string => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const serverType = process.env.REACT_APP_SERVER_TYPE;
  const webhookPath = serverType === 'test' ? 'webhook-test' : 'webhook';

  // Clean the ID by removing any quotes
  const cleanId = id.replace(/["']/g, '');

  // Return the direct URL to fetch the PDF binary
  return `${baseUrl}/${webhookPath}/agent_gg_aukik/get?route=pdf_url&id=${cleanId}`;
};

export const getPdfInfoUrl = (id: string): string => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const serverType = process.env.REACT_APP_SERVER_TYPE;
  const webhookPath = serverType === 'test' ? 'webhook-test' : 'webhook';

  // Clean the ID by removing any quotes
  const cleanId = id.replace(/["']/g, '');

  // Return the URL to fetch PDF info
  return `${baseUrl}/${webhookPath}/agent_gg_aukik/get?route=pdf_info&id=${cleanId}`;
};

export const fetchPdfInfo = async (id: string) => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No JWT token found in localStorage');
      return null;
    }

    const url = getPdfInfoUrl(id);
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch PDF info: ${response.status}`);
    }

    const data = await response.json();
    if (data && data.length > 0 && data[0].response) {
      return {
        title: data[0].response.fileName || '',
        type: data[0].response.fileType || 'pdf'
      };
    }
    return null;
  } catch (error) {
    console.error("Error fetching PDF info:", error);
    return null;
  }
};

/**
 * Fetch PDF document as binary data with proper JWT authentication
 * @param id MongoDB document ID
 * @returns Promise with blob URL that can be used to display the PDF
 */
export const fetchPdfBinary = async (id: string): Promise<string | null> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No JWT token found in localStorage');
      return null;
    }

    // Get the direct URL
    const directUrl = getPdfDirectUrl(id);

    // Fetch the binary data with authorization header
    const response = await axios.get(directUrl, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      responseType: 'blob' // Important: we want a binary response
    });

    // Create a blob URL that can be used in an iframe, embed, etc.
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    console.log("#################################################################### This is the blob url", blobUrl);
    return blobUrl;
  } catch (error) {
    console.error('Error fetching PDF binary:', error);
    return null;
  }
};

/**
 * Fetches chat sessions from the API
 * @param limit Number of sessions to retrieve (default: 10)
 * @param skip Number of sessions to skip for pagination (default: 0)
 * @returns Promise that resolves to an array of session data
 */
export const fetchChatSessions = async (
  limit: number = 10,
  skip: number = 0
): Promise<SessionData[]> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No JWT token found in localStorage');
      throw new Error('Authentication error. Please log in again.');
    }

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const serverType = process.env.REACT_APP_SERVER_TYPE;
    const webhookPath = serverType === 'test' ? 'webhook-test' : 'webhook';

    // Construct the sessions API endpoint
    const sessionsEndpoint = `${baseUrl}/${webhookPath}/agent_gg_aukik/chatData`;

    // Make the GET request with query parameters
    const response = await axios.get(sessionsEndpoint, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      params: {
        route: 'session_list',
        limit,
        skip
      }
    });

    // Check if we have a valid response
    if (Array.isArray(response.data)) {
      // Filter out empty or invalid sessions
      const validSessions = response.data.filter(session => {
        // Check if session has required properties
        return session &&
               typeof session === 'object' &&
               session._id &&
               session.sessionId &&
               Array.isArray(session.messages);
      });

      return validSessions;
    } else {
      console.error('Invalid response format from session list API:', response.data);
      throw new Error('Invalid response format from the server');
    }
  } catch (error) {
    console.error('Error fetching chat sessions:', error);
    throw error;
  }
};

/**
 * Converts API session data to the ExtendedChat format used by the frontend
 * @param sessionData Session data from the API
 * @returns ExtendedChat object
 */
export const convertSessionToChat = (sessionData: SessionData) => {
  if (!sessionData || !sessionData._id || !sessionData.sessionId || !Array.isArray(sessionData.messages)) {
    // Return a placeholder chat if session data is invalid
    console.warn('Received invalid session data:', sessionData);
    return {
      id: Date.now().toString(),
      title: 'New Chat',
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
      sessionId: sessionData?.sessionId || uuidv4()
    };
  }

  // Extract the first message content to use as title
  let title = 'New Chat';
  if (sessionData.messages.length > 0 && sessionData.messages[0].type === 'human') {
    const firstMessageContent = sessionData.messages[0].data?.content || '';
    title = firstMessageContent.slice(0, 30) + (firstMessageContent.length > 30 ? '...' : '');
  }

  // Convert messages to the format expected by the frontend
  const messages = sessionData.messages.map((msg, index) => {
    // Skip invalid messages
    if (!msg || !msg.data) {
      console.warn('Skipping invalid message:', msg);
      return null;
    }

    // For AI messages, parse the JSON content to extract the actual response
    if (msg.type === 'ai') {
      try {
        const content = msg.data.content || '';
        let parsedContent;
        try {
          parsedContent = JSON.parse(content);
        } catch (e) {
          console.warn('Failed to parse AI message content:', e);
          return {
            id: `ai-${Date.now()}-${index}`,
            role: 'assistant' as const,
            content: content || 'Error parsing AI response',
            timestamp: Date.now()
          };
        }

        const aiResponse = parsedContent?.output?.response || '';

        // Check if documents were used
        const documentsUsed = parsedContent?.output?.documentsUsed || [];

        // Convert documents to the source format expected by the frontend
        const sources = documentsUsed.map((doc: any, docIndex: number) => ({
          id: doc.mongodb_id || `source-${docIndex}`,
          title: doc.fileName || `Document ${docIndex + 1}`,
          page: doc.page_number || 1,
          content: `Content from ${doc.fileName || 'document'}, page ${doc.page_number || 1}`,
          type: (doc.fileType || 'text').toLowerCase() as 'pdf' | 'doc' | 'text',
        }));

        return {
          id: `ai-${Date.now()}-${index}`,
          role: 'assistant' as const,
          content: aiResponse,
          timestamp: Date.now(),
          sources: sources.length > 0 ? sources : undefined
        };
      } catch (error) {
        console.error('Error processing AI message:', error);
        return {
          id: `ai-${Date.now()}-${index}`,
          role: 'assistant' as const,
          content: 'Error parsing AI response',
          timestamp: Date.now()
        };
      }
    } else {
      // Human messages are simpler
      return {
        id: `human-${Date.now()}-${index}`,
        role: 'user' as const,
        content: msg.data.content || '',
        timestamp: Date.now()
      };
    }
  }).filter(Boolean); // Remove any null messages

  return {
    id: sessionData._id,
    title: title,
    messages: messages,
    createdAt: Date.now(), // We don't have this in the API response
    updatedAt: Date.now(), // We don't have this in the API response
    sessionId: sessionData.sessionId
  };
};

/**
 * Deletes a chat session
 * @param sessionId The session ID to delete
 * @returns Promise that resolves to a success indicator
 */
export const deleteChatSession = async (sessionId: string): Promise<boolean> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No JWT token found in localStorage');
      throw new Error('Authentication error. Please log in again.');
    }

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const serverType = process.env.REACT_APP_SERVER_TYPE;
    const webhookPath = serverType === 'test' ? 'webhook-test' : 'webhook';

    // Construct the sessions API endpoint
    const deleteEndpoint = `${baseUrl}/${webhookPath}/agent_gg_aukik/chatData`;

    // Make the DELETE request with the session ID as a parameter
    const response = await axios.delete(deleteEndpoint, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      params: {
        route: 'delete_session',
        sessionId
      }
    });

    return response.status === 200;
  } catch (error) {
    console.error('Error deleting chat session:', error);
    throw error;
  }
};

/**
 * Sends audio data to the voice transcription service
 * @param audioBlob The recorded audio data as a Blob
 * @returns Promise that resolves to the transcribed text
 */
export const transcribeAudio = async (audioBlob: Blob): Promise<string> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No JWT token found in localStorage');
      throw new Error('Authentication error. Please log in again.');
    }

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const serverType = process.env.REACT_APP_SERVER_TYPE;
    const webhookPath = serverType === 'test' ? 'webhook-test' : 'webhook';

    // Construct the transcription API endpoint
    const transcriptionEndpoint = `${baseUrl}/${webhookPath}/agent_gg_aukik/voice_transcription`;

    // Create form data to send the audio blob
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');

    // Make the POST request
    const response = await axios.post(transcriptionEndpoint, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });

    // Check if the response contains the transcription in the "text" field
    if (response.data && response.data.text) {
      return response.data.text;
    } else {
      throw new Error('Invalid response format from the server');
    }
  } catch (error) {
    console.error('Error transcribing audio:', error);
    throw error;
  }
};
